/**
 * Medication Administration Form Component
 * 
 * Form for adding/editing medication administration requests
 * Features:
 * - Add new medication administration request
 * - Edit existing request
 * - Digital signature capture
 * - Form validation
 * - Works for both Medication (type=1) and Non-Medication (type=0)
 */

import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Container, Form, Button } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { 
  submitMedicationForm, 
  fetchMedicationList,
  selectFormSubmitting, 
  selectFormError, 
  selectFormSuccessMessage,
  selectMedicationById,
  selectNonMedicationById,
  clearErrors,
  clearFormSuccess
} from '../../../../store/medication/medicationSlice';

import LoaderModal from '../../../../components/loaderModal';
import SignatureCanvas from 'react-signature-canvas';
import { handleImageUpload } from '../../../../api/uploads';

//Sweet alert
import Swal from 'sweetalert2'

const MedicationForm = ({ type = 1 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  
  // Redux state
  const submitting = useSelector(selectFormSubmitting);
  const error = useSelector(selectFormError);
  const successMessage = useSelector(selectFormSuccessMessage);
  
  // Get existing medication data from list if in edit mode
  const existingMedication = useSelector(
    type === 1 ? selectMedicationById(id) : selectNonMedicationById(id)
  );

  const [formData, setFormData] = useState({
    parent_name: '',
    medication_name: '',
    times_to_be_administered: '',
    date_start: '',
    date_end: '',
    expiry_date: '',
    parents_consent_and_instruction: '',
    additional_instructions: '',
    reason_for_administering: '',
    signiture_of_parent: '',
    signature_date: new Date().toISOString().split('T')[0],
    doctor_note: '',
    type: type
  });

  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [doctorNoteUploading, setDoctorNoteUploading] = useState(false);
  
  // Signature pad ref
  const signaturePadRef = useRef(null);

  // Load medication list if not already loaded (for edit mode)
  useEffect(() => {
    if (id && !existingMedication && !dataLoaded) {
      // Fetch list if we don't have the data yet
      dispatch(fetchMedicationList({ type: type, page: 1, limit: 100 }));
      setDataLoaded(true);
    }
  }, [id, existingMedication, dataLoaded, dispatch, type]);

  // Populate form with existing data when in edit mode
  useEffect(() => {
    if (id && existingMedication) {
      setIsEditMode(true);
      
      // Format dates for input fields
      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        return dateString.split('T')[0];
      };
      
      // Format time for input field (remove seconds if present)
      const formatTimeForInput = (timeString) => {
        if (!timeString) return '';
        const parts = timeString.split(':');
        return `${parts[0]}:${parts[1]}`; // Return HH:MM only
      };

      setFormData({
        parent_name: existingMedication.parent_name || '',
        medication_name: existingMedication.medication_name || '',
        times_to_be_administered: formatTimeForInput(existingMedication.times_to_be_administered),
        date_start: formatDateForInput(existingMedication.date_start),
        date_end: formatDateForInput(existingMedication.date_end),
        expiry_date: formatDateForInput(existingMedication.expiry_date),
        parents_consent_and_instruction: existingMedication.parents_consent_and_instruction || '',
        additional_instructions: existingMedication.additional_instructions || '',
        reason_for_administering: existingMedication.reason_for_administering || '',
        signiture_of_parent: existingMedication.signiture_of_parent || '',
        signature_date: formatDateForInput(existingMedication.signature_date),
        doctor_note: existingMedication.doctor_note || '',
        type: type
      });
    }
  }, [id, existingMedication, type]);

  // Handle success message
  useEffect(() => {
    if (successMessage) {
      Swal.fire({
        title: 'Success!',
        text: successMessage,
        icon: 'success',
        confirmButtonText: 'Okay'
      }).then(() => {
        dispatch(clearFormSuccess());
        if (type === 1) {
          navigate('/dashboard/app/medication/list');
        } else {
          navigate('/dashboard/app/non-medication/list');
        }
      });
    }
  }, [successMessage, dispatch, navigate, type]);

  // Handle form errors
  useEffect(() => {
    if (error) {
      Swal.fire({
        title: 'Error',
        text: error,
        icon: 'error',
        confirmButtonText: 'Okay'
      }).then(() => {
        dispatch(clearErrors());
      });
    }
  }, [error, dispatch]);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific field error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.parent_name.trim()) {
      newErrors.parent_name = 'Parent name is required';
    }

    if (!formData.medication_name.trim()) {
      newErrors.medication_name = type === 1 ? 'Medication name is required' : 'Non-medication name is required';
    }

    if (!formData.times_to_be_administered) {
      newErrors.times_to_be_administered = 'Administration time is required';
    }

    if (!formData.date_start) {
      newErrors.date_start = 'Start date is required';
    }

    if (!formData.date_end) {
      newErrors.date_end = 'End date is required';
    }

    if (formData.date_start && formData.date_end) {
      if (new Date(formData.date_start) > new Date(formData.date_end)) {
        newErrors.date_end = 'End date must be after start date';
      }
    }

    if (!formData.parents_consent_and_instruction.trim()) {
      newErrors.parents_consent_and_instruction = 'Parent consent and instruction is required';
    }

    if (!formData.reason_for_administering.trim()) {
      newErrors.reason_for_administering = 'Reason for administering is required';
    }

    if (!formData.signiture_of_parent.trim()) {
      newErrors.signiture_of_parent = 'Parent signature is required';
    }

    if (!formData.signature_date) {
      newErrors.signature_date = 'Signature date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Clear signature pad
  const handleClearSignature = () => {
    // Clear the saved signature URL first
    setFormData(prev => ({
      ...prev,
      signiture_of_parent: ''
    }));
    
    // Clear the canvas if it exists (will exist after re-render)
    setTimeout(() => {
      if (signaturePadRef.current) {
        signaturePadRef.current.clear();
      }
    }, 100);
    
    // Clear any errors
    if (errors.signiture_of_parent) {
      setErrors(prev => ({
        ...prev,
        signiture_of_parent: ''
      }));
    }
  };

  // Save signature and upload to AWS
  const handleSaveSignature = async () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      try {
        setSignatureUploading(true);
        
        // Get signature as data URL
        const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
        
        // Convert data URL to Blob
        const response = await fetch(signatureDataURL);
        const blob = await response.blob();
        
        // Create a file from blob
        const timestamp = new Date().getTime();
        const fileName = `signature_${timestamp}.png`;
        const file = new File([blob], fileName, { type: 'image/png' });
        
        // Upload to AWS
        const fileKey = 'signatures/';
        const uploadedUrl = await handleImageUpload(file, fileKey, 'image/png');
        
        if (uploadedUrl) {
          // Update form data with the uploaded file URL
          setFormData(prev => ({
            ...prev,
            signiture_of_parent: uploadedUrl
          }));
          
          // Clear error if exists
          if (errors.signiture_of_parent) {
            setErrors(prev => ({
              ...prev,
              signiture_of_parent: ''
            }));
          }
          
          Swal.fire({
            title: 'Success!',
            text: 'Signature saved successfully',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        }
      } catch (error) {
        console.error('Error uploading signature:', error);
        Swal.fire({
          title: 'Error',
          text: 'Failed to upload signature. Please try again.',
          icon: 'error',
          confirmButtonText: 'Okay'
        });
      } finally {
        setSignatureUploading(false);
      }
    } else {
      Swal.fire({
        title: 'Empty Signature',
        text: 'Please draw your signature first',
        icon: 'warning',
        confirmButtonText: 'Okay'
      });
    }
  };

  // Handle doctor note file upload
  const handleDoctorNoteUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire({
        title: 'Invalid File Type',
        text: 'Please upload a PDF, PNG, or JPG file',
        icon: 'error',
        confirmButtonText: 'Okay'
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        title: 'File Too Large',
        text: 'File size should not exceed 5MB',
        icon: 'error',
        confirmButtonText: 'Okay'
      });
      return;
    }

    try {
      setDoctorNoteUploading(true);

      // Upload to AWS
      const fileKey = 'doctor-notes/';
      const uploadedUrl = await handleImageUpload(file, fileKey, file.type);

      if (uploadedUrl) {
        // Update form data with the uploaded file URL
        setFormData(prev => ({
          ...prev,
          doctor_note: uploadedUrl
        }));

        Swal.fire({
          title: 'Success!',
          text: 'Doctor note uploaded successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error) {
      console.error('Error uploading doctor note:', error);
      Swal.fire({
        title: 'Error',
        text: 'Failed to upload doctor note. Please try again.',
        icon: 'error',
        confirmButtonText: 'Okay'
      });
    } finally {
      setDoctorNoteUploading(false);
    }
  };

  // Remove doctor note
  const handleRemoveDoctorNote = () => {
    Swal.fire({
      title: 'Remove Doctor Note?',
      text: 'Are you sure you want to remove this file?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, remove it'
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData(prev => ({
          ...prev,
          doctor_note: ''
        }));
      }
    });
  };

  // Get file extension from URL
  const getFileExtension = (url) => {
    if (!url) return '';
    const parts = url.split('.');
    return parts[parts.length - 1].toLowerCase();
  };

  // Check if file is PDF
  const isPDF = (url) => {
    return getFileExtension(url) === 'pdf';
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Map form field names to API parameter names
      const submitData = {
        parent_name: formData.parent_name,
        medication_name: formData.medication_name, 
        times_to_be_administered: formData.times_to_be_administered,
        date_start: formData.date_start,
        date_end: formData.date_end,
        expiry_date: formData.expiry_date,
        parents_consent_and_instruction: formData.parents_consent_and_instruction,
        additional_instructions: formData.additional_instructions,
        reason_for_administering: formData.reason_for_administering,
        signiture_of_parent: formData.signiture_of_parent,
        signature_date: formData.signature_date,
        doctor_note: formData.doctor_note,
        type: type
      };
      
      if (isEditMode) {
        submitData.id = id;
      }
      
      dispatch(submitMedicationForm(submitData));
    }
  };

  // Get form title
  const getFormTitle = () => {
    if (type === 1) {
      return isEditMode ? 'Edit Medication Administration' : 'Administration of Medication';
    } else {
      return isEditMode ? 'Edit Non-Medication Administration' : 'Administration of Non-Medication';
    }
  };

  // Get medication options for non-medication type
  const nonMedicationOptions = [
    'Lotion',
    'Sunscreen', 
    'Insect repellent',
    'Lip balm',
    'Diaper rash cream'
  ];

  return (
    <>
      <LoaderModal showLoader={submitting} />
      <div id='content-page' className='content-page'>
        <Container>
          <Row>
            <Col sm="12">
              <Card>
                <Card.Header>
                  <div className="header-title">
                    <h4 className="card-title">
                      <i className={`${type === 1 ? 'ri-medicine-bottle-line' : 'ri-first-aid-kit-line'} me-2`}></i>
                      {getFormTitle()}
                    </h4>
                  </div>
                </Card.Header>
                <Card.Body>
                  {/* Parental Approval Section */}
                  <div className="bg-light p-3 rounded mb-4">
                    <h6 className="mb-2">Parental Approval:</h6>
                    <p className="mb-2">
                      {/* This used to be a literal string of underscores - it
                          looked like a fill-in-the-blank but wasn't an input
                          at all, so nothing happened when someone tried to
                          type into it. It's the same parent_name field as the
                          "Parent Name" input below (shared name/value/onChange),
                          just editable right where the sentence expects it -
                          typing here or in that field updates both. */}
                      I{' '}
                      <input
                        type="text"
                        name="parent_name"
                        value={formData.parent_name}
                        onChange={handleInputChange}
                        placeholder="parent's name"
                        aria-label="Parent name"
                        style={{
                          border: 'none',
                          borderBottom: `1px solid ${errors.parent_name ? '#dc3545' : '#6c757d'}`,
                          background: 'transparent',
                          width: '220px',
                          padding: '0 4px',
                          font: 'inherit',
                          color: 'inherit',
                        }}
                      />{' '}
                      request and give permission for the staff to administer
                      {type === 1 ? ' oral medication' : ' non-medication'} to my child, according to the
                      procedures outlined and following the detailed instructions.
                    </p>
                    {errors.parent_name && (
                      <div className="text-danger small mb-2">{errors.parent_name}</div>
                    )}
                    
                    {type === 0 && (
                      <div className="mt-3">
                        <Row>
                          {nonMedicationOptions.map((option, index) => (
                            <Col md={2} sm={4} xs={6} key={index} className="mb-2">
                              <div className="text-center">
                                <small>{option}</small>
                              </div>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Parent Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="parent_name"
                            value={formData.parent_name}
                            onChange={handleInputChange}
                            placeholder="Enter parent name"
                            isInvalid={!!errors.parent_name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.parent_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            {type === 1 ? 'Name of Medication' : 'Name of Non-Medication'} 
                            <span className="text-danger">*</span>
                          </Form.Label>
                          {type === 0 ? (
                            <Form.Select
                              name="medication_name"
                              value={formData.medication_name}
                              onChange={handleInputChange}
                              isInvalid={!!errors.medication_name}
                            >
                              <option value="">Select non-medication type</option>
                              {nonMedicationOptions.map((option, index) => (
                                <option key={index} value={option}>{option}</option>
                              ))}
                            </Form.Select>
                          ) : (
                            <Form.Control
                              type="text"
                              name="medication_name"
                              value={formData.medication_name}
                              onChange={handleInputChange}
                              placeholder="Enter medication name"
                              isInvalid={!!errors.medication_name}
                            />
                          )}
                          <Form.Control.Feedback type="invalid">
                            {errors.medication_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Time to be Administered <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="time"
                            name="times_to_be_administered"
                            value={formData.times_to_be_administered}
                            onChange={handleInputChange}
                            isInvalid={!!errors.times_to_be_administered}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.times_to_be_administered}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="date"
                            name="date_start"
                            value={formData.date_start}
                            onChange={handleInputChange}
                            isInvalid={!!errors.date_start}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.date_start}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="date"
                            name="date_end"
                            value={formData.date_end}
                            onChange={handleInputChange}
                            isInvalid={!!errors.date_end}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.date_end}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Expiry Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="expiry_date"
                            value={formData.expiry_date}
                            onChange={handleInputChange}
                            isInvalid={!!errors.expiry_date}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.expiry_date}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            Medication expiration date (if applicable)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      
                      <Col md={8}>
                        <Form.Group className="mb-3">
                          <Form.Label>Doctor's Note</Form.Label>
                          
                          {/* Show existing doctor note if available */}
                          {formData.doctor_note && (
                            <div className="mb-2 p-3 border rounded bg-light">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center gap-2">
                                  <i className={`${isPDF(formData.doctor_note) ? 'ri-file-pdf-line' : 'ri-image-line'} fs-4 text-primary`}></i>
                                  <div>
                                    <small className="text-success d-block">
                                      <i className="ri-checkbox-circle-fill me-1"></i>
                                      File uploaded
                                    </small>
                                    <small className="text-muted">
                                      {getFileExtension(formData.doctor_note).toUpperCase()} file
                                    </small>
                                  </div>
                                </div>
                                <div className="d-flex gap-1">
                                  {!isPDF(formData.doctor_note) && (
                                    <Button
                                      variant="outline-info"
                                      size="sm"
                                      onClick={() => window.open(formData.doctor_note, '_blank')}
                                      title="View Image"
                                    >
                                      <i className="ri-eye-line"></i>
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => window.open(formData.doctor_note, '_blank')}
                                    title="Download"
                                  >
                                    <i className="ri-download-line"></i>
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleRemoveDoctorNote}
                                    title="Remove"
                                  >
                                    <i className="ri-delete-bin-line"></i>
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {/* File upload input */}
                          <Form.Control
                            type="file"
                            accept=".pdf,.png,.jpg,.jpeg"
                            onChange={handleDoctorNoteUpload}
                            disabled={doctorNoteUploading}
                          />
                          {doctorNoteUploading && (
                            <div className="mt-2">
                              <span className="spinner-border spinner-border-sm me-2"></span>
                              <small className="text-muted">Uploading...</small>
                            </div>
                          )}
                          <Form.Text className="text-muted">
                            Upload doctor's note (PDF, PNG, or JPG - Max 5MB)
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Parent's Consent and Instruction <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="parents_consent_and_instruction"
                            value={formData.parents_consent_and_instruction}
                            onChange={handleInputChange}
                            placeholder="Enter detailed instructions and consent"
                            isInvalid={!!errors.parents_consent_and_instruction}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.parents_consent_and_instruction}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>Additional Instructions</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="additional_instructions"
                            value={formData.additional_instructions}
                            onChange={handleInputChange}
                            placeholder="Any additional instructions (optional)"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>
                            Reason for Administering {type === 1 ? 'Medication' : 'Non-Medication'} 
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            name="reason_for_administering"
                            value={formData.reason_for_administering}
                            onChange={handleInputChange}
                            placeholder="Enter the reason for administering"
                            isInvalid={!!errors.reason_for_administering}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.reason_for_administering}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Signature of Parent <span className="text-danger">*</span></Form.Label>
                          
                          {/* Show existing signature if available (edit mode) */}
                          {formData.signiture_of_parent && formData.signiture_of_parent.startsWith('http') ? (
                            <div className="mb-3">
                              <div className="border rounded p-3 bg-light text-center">
                                <img 
                                  src={formData.signiture_of_parent} 
                                  alt="Saved signature" 
                                  style={{ 
                                    maxWidth: '100%', 
                                    height: 'auto', 
                                    maxHeight: '120px',
                                    display: 'block',
                                    margin: '0 auto'
                                  }}
                                />
                              </div>
                              <div className="d-flex justify-content-between align-items-center mt-2">
                                <small className="text-success">
                                  <i className="ri-checkbox-circle-fill me-1"></i>
                                  Signature saved
                                </small>
                                <Button 
                                  variant="outline-warning" 
                                  size="sm"
                                  onClick={handleClearSignature}
                                  type="button"
                                >
                                  <i className="ri-pencil-line me-1"></i>
                                  Change Signature
                                </Button>
                              </div>
                            </div>
                          ) : null}
                          
                          {/* Signature Pad - Only show if no signature saved */}
                          {!formData.signiture_of_parent || !formData.signiture_of_parent.startsWith('http') ? (
                            <>
                              <div className="border rounded" style={{ 
                                border: errors.signiture_of_parent ? '1px solid #dc3545' : '1px solid #dee2e6',
                                backgroundColor: '#fff'
                              }}>
                                <SignatureCanvas
                                  ref={signaturePadRef}
                                  canvasProps={{
                                    className: 'signature-canvas',
                                    style: { width: '100%', height: '150px' }
                                  }}
                                  backgroundColor="rgb(255, 255, 255)"
                                />
                              </div>
                              
                              <div className="d-flex gap-2 mt-2">
                                <Button 
                                  variant="outline-danger" 
                                  size="sm"
                                  onClick={handleClearSignature}
                                  type="button"
                                >
                                  <i className="ri-eraser-line me-1"></i>
                                  Clear
                                </Button>
                                <Button 
                                  variant="outline-primary" 
                                  size="sm"
                                  onClick={handleSaveSignature}
                                  disabled={signatureUploading}
                                  type="button"
                                >
                                  {signatureUploading ? (
                                    <>
                                      <span className="spinner-border spinner-border-sm me-1"></span>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <i className="ri-save-line me-1"></i>
                                      Save Signature
                                    </>
                                  )}
                                </Button>
                              </div>
                              
                              {errors.signiture_of_parent && (
                                <div className="text-danger small mt-1">
                                  {errors.signiture_of_parent}
                                </div>
                              )}
                              <Form.Text className="text-muted">
                                Draw your signature above and click "Save Signature" before submitting the form
                              </Form.Text>
                            </>
                          ) : null}
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Signature Date <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="date"
                            name="signature_date"
                            value={formData.signature_date}
                            onChange={handleInputChange}
                            isInvalid={!!errors.signature_date}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.signature_date}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="d-flex gap-2 justify-content-end">
                      <Button 
                        variant="secondary" 
                        onClick={() => navigate(-1)}
                        disabled={submitting}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="primary" 
                        type="submit"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </span>
                            {isEditMode ? 'Updating...' : 'Submitting...'}
                          </>
                        ) : (
                          <>
                            <i className="ri-save-line me-1"></i>
                            {isEditMode ? 'Update Request' : 'Submit Request'}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  )
}

export default MedicationForm